/**
 * Chargement des textes des formations et des fiches pratiques.
 *
 * Un fichier JSON par formation (ou fiche) et par langue, importé à la
 * demande : la page d'une formation ne télécharge que SES textes dans SA
 * langue (≈ 15 Ko), jamais les 8 formations × 6 langues.
 * Langue absente → repli sur le français (même règle que le reste du site).
 */
import { FORMATIONS, type Formation } from "./catalogue";
import type { TextesFiche, TextesFormation, TextesModule } from "./types";
import videos from "./videos.json";

const chargeursFormations = import.meta.glob<TextesFormation>("./textes/*/*.json", {
  import: "default",
});
const chargeursFiches = import.meta.glob<TextesFiche>("./fiches/*/*.json", {
  import: "default",
});

async function charger<T>(
  chargeurs: Record<string, () => Promise<T>>,
  dossier: string,
  langue: string,
  id: string,
): Promise<T | null> {
  const chargeur =
    chargeurs[`./${dossier}/${langue}/${id}.json`] ?? chargeurs[`./${dossier}/fr/${id}.json`];
  return chargeur ? chargeur() : null;
}

export function chargerTextesFormation(slug: string, langue: string) {
  return charger(chargeursFormations, "textes", langue, slug);
}

export function chargerFiche(id: string, langue: string) {
  return charger(chargeursFiches, "fiches", langue, id);
}

/** Textes de toutes les formations (page index : titres, résumés, compteurs). */
export async function chargerToutesLesFormations(langue: string) {
  const textes = await Promise.all(FORMATIONS.map((f) => chargerTextesFormation(f.slug, langue)));
  return FORMATIONS.flatMap((formation, i) => {
    const t = textes[i];
    return t ? [{ formation, textes: t }] : [];
  });
}

// ─── Vidéos publiées ───────────────────────────────────────────────────────
// `videos.json` est écrit par scripts/formations-videos.mjs à chaque vidéo
// publiée (durée réelle lue par ffprobe). Une vidéo absente du manifeste n'est
// pas affichée.

export interface VideoPubliee {
  /** Durée en secondes. */
  duree: number;
  largeur: number;
  hauteur: number;
  /** Date de publication (AAAA-MM-JJ) — VideoObject.uploadDate. */
  publiee: string;
}

const VIDEOS = videos as Record<string, VideoPubliee>;

export function videoPubliee(id: string | undefined): VideoPubliee | null {
  return (id && VIDEOS[id]) || null;
}

export function urlVideo(id: string) {
  return `/videos/formations/${id}.mp4`;
}

export function urlAffiche(id: string) {
  return `/videos/formations/${id}.jpg`;
}

export function urlFichePdf(id: string, langue: string) {
  return `/fiches-pratiques/${langue}/donoor-fiche-${id}.pdf`;
}

// ─── Durées et compteurs (calculés, jamais saisis à la main) ──────────────

/** Vitesse de lecture retenue pour le pas à pas écrit. */
const MOTS_PAR_MINUTE = 200;

function compterMots(texte: string) {
  return texte.split(/\s+/).filter(Boolean).length;
}

/** Durée d'un module en minutes : vidéo + lecture des étapes, arrondie à la minute supérieure. */
export function dureeModule(textes: TextesModule, tutoriel?: string) {
  const mots = textes.etapes.reduce(
    (total, e) => total + compterMots(e.titre) + compterMots(e.texte),
    compterMots(textes.astuce ?? ""),
  );
  const secondes = (videoPubliee(tutoriel)?.duree ?? 0) + (mots / MOTS_PAR_MINUTE) * 60;
  return Math.max(1, Math.ceil(secondes / 60));
}

export function compteursFormation(formation: Formation, textes: TextesFormation) {
  let minutes = 0;
  let videosPubliees = 0;
  let articles = 0;
  for (const module of formation.modules) {
    const t = textes.modules[module.id];
    if (!t) continue;
    minutes += dureeModule(t, module.tutoriel);
    if (videoPubliee(module.tutoriel)) videosPubliees += 1;
    articles += t.liens.filter((l) => l.type !== "fiche").length;
  }
  return {
    modules: formation.modules.length,
    videos: videosPubliees,
    minutes,
    fiches: formation.fiches.length,
    articles,
  };
}
